import useNavigation from '../../../../hook/useNavigation';
import { UFOODX_BASE_URL } from '../../../../utils/constants';
import { isEmptyData, cleanText, encodeValue } from '../../../../utils/helper';
import { DeliveryIcon, DineInIcon, TakeawayIcon } from '../../../../utils/svg';
import { useDispatch, useSelector } from 'react-redux';
import { getOrderTypeOptions, setOrder, tableNumberAuto } from '../../../../store/reducers/order';
import { useEffect, useMemo, useState } from 'react';
import InputTableNumber from '../InputTableNumber';
import QRScanner from '../../QRScanner';
import { removeCart } from '../../../../store/reducers/cart';
import OutletClose from '../OutletClose';
import OutletClosedCard from '../../../../components/Outlet/OutletClose/index';
import ContinueTableNumber from '../ContinueTableNumber';

const types = [
	{
		icon: DeliveryIcon,
		label: 'Delivery',
		type: 'delivery',
	},
	{
		icon: TakeawayIcon,
		label: 'Self Pickup',
		type: 'pickup',
	},
	{
		icon: DineInIcon,
		label: 'Dine In',
		type: 'dinein',
	},
];

const Service = (props) => {
	const navigate = useNavigation();
	const dispatch = useDispatch();

	const { details: outlet } = useSelector((store) => store?.outlet);
	const { orderTypeOptions, selectedOrderType } = useSelector((store) => store?.order);
	const company = useSelector((store) => store?.company);
	const user = useSelector((store) => store?.user);
	const [isShowTableNumber, setIsShowTableNumber] = useState(false);
	const [isShowScanner, setShowScanner] = useState(false);
	const [openOutletClose, setOpenOutletClose] = useState(false);
	const [showContinueTableModal, setShowContinueTableModal] = useState(false);
	const prevType = cleanText(selectedOrderType?.av_name);

	const { tableNumber } = useSelector((store) => store?.order);

	const availabilityMap = useMemo(() => {
		if (!orderTypeOptions?.length) return {};

		return orderTypeOptions.reduce((acc, item) => {
			const key = cleanText(item?.av_name);
			acc[key] = item;
			return acc;
		}, {});
	}, [orderTypeOptions]);

	const { isOpen = '0' } = props;
	const isClosed = !Boolean(isOpen) || isOpen === '0';

  const handleRemoveCart = async () => {
    await dispatch(
      removeCart({
        customerID: encodeValue(user?.customerID),
        storeID: outlet?.storeID,
      }),
    ).unwrap();
  };

	const handleClick = async (typeKey = null) => {
		if (isEmptyData(typeKey) || isEmptyData(outlet?.storeSlug)) return;

		const isOpen = isOutletOpen(
			outlet?.outletOpenTime,
			outlet?.outletCloseTime
		);

		if (!isOpen) {
			setOpenOutletClose(true);
			return;
		}
		const selectedAvailability = availabilityMap[typeKey];

		if (!selectedAvailability) return;
		dispatch(setOrder({ selectedOrderType: selectedAvailability }));

		const nameOrderType = cleanText(selectedAvailability?.av_name);

		const url = `${UFOODX_BASE_URL}/${outlet.storeSlug}/${nameOrderType}`;

		if (nameOrderType === 'dinein') {
			const isExpired =
				!tableNumber?.expired_at ||
				Date.now() > tableNumber?.expired_at;

			if (isExpired || isEmptyData(tableNumber?.table_number)) {
				handleNewTableFlow();
				return;
			}

			setShowContinueTableModal(true);
			return;
		}
		navigate.push(url);
	};

	const handleGetOrderTypeOptions = () => {
		dispatch(
			getOrderTypeOptions({
				storeID: outlet?.storeID,
			})
		);
	};

	useEffect(() => {
		if (!outlet?.storeID) return;
		handleGetOrderTypeOptions();
	}, [outlet?.storeID]);

	const handleCloseTableNumber = () => {
		setIsShowTableNumber(false);
	};

	const isOutletOpen = (openTime, closeTime) => {
		const now = new Date();

		const [openHour, openMinute] = openTime.split(':').map(Number);
		const [closeHour, closeMinute] = closeTime.split(':').map(Number);

		const open = new Date();
		open.setHours(openHour, openMinute, 0);

		const close = new Date();
		close.setHours(closeHour, closeMinute, 0);

		return now >= open && now <= close;
	};

	const handleNewTableFlow = async () => {
		await dispatch(setOrder({ tableNumber: {} }));
		if (isEmptyData(outlet?.dinein_qr_code)) {
			dispatch(
				tableNumberAuto({
					companyId: Number(company?.companyID),
					outletId: Number(outlet?.storeID),
					brandId: Number(outlet?.brand_id),
				})
			);
			return await continueTableNumber();
		}

		if (outlet?.dinein_qr_code) {
			setIsShowTableNumber(true);
			return;
		}

		setShowScanner(true);
	};

	const continueTableNumber = async () => {
		setShowContinueTableModal(false);
		const url = `${UFOODX_BASE_URL}/${outlet?.storeSlug}/dinein`;
		navigate.push(url);
	};

	return (
		<div className='flex justify-between gap-4 mb-4'>
			{types?.map((t, index) => {
				const Icon = t.icon;
				const available = !!availabilityMap[t?.type];
				const isDisabled = !available || isClosed;

				return (
					<div
						key={index}
						className={`
                        gap-1.5 py-2 px-4 flex flex-col items-center justify-center rounded-md w-full min-w-fit shadow-sm
                        ${isDisabled ? "bg-gray-100 cursor-not-allowed opacity-60" : "bg-white cursor-pointer"}
                    `}
            onClick={async (e) => {
              if (isDisabled) return;
              e.stopPropagation();

              try {
                if (!isEmptyData(prevType) && cleanText(t?.type) !== cleanText(prevType)) {
                  await handleRemoveCart();
                }
                handleClick(t?.type);
              } catch (error) {
                console.log('error: ', error);                
              }
            }}
					>
						<div className={isDisabled ? "grayscale" : ""}>
							<Icon />
						</div>
						<div className={`text-xs font-normal ${isDisabled ? "text-gray-400" : "text-gray-700"}`}>
							{t?.label}
						</div>
					</div>
				);
			})}

			<InputTableNumber open={isShowTableNumber} onClose={handleCloseTableNumber} />
			{isShowScanner && <QRScanner onClose={() => setShowScanner(false)} />}
			{openOutletClose && (
				<OutletClosedCard open={openOutletClose} onBackHome={() => setOpenOutletClose(false)} />
			)}

			{showContinueTableModal && (
				<ContinueTableNumber
					open={showContinueTableModal}
					onClose={() => setShowContinueTableModal(false)}
					onContinue={() => continueTableNumber()}
					onChangeTable={async () => {
						setShowContinueTableModal(false);
						await handleNewTableFlow();
					}}
					tableNumber={tableNumber?.table_number}
				/>
			)}
		</div>
	);
};

export default Service;